'use client'

import { useEffect, useState } from 'react'
import { Settings2, Volume2, VolumeX, KeyRound, Coins, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DEFAULT_LLM_SETTINGS,
  loadLlmProviderSettings,
  saveLlmProviderSettings,
  type MafiaLlmProviderSettings,
} from '../lib/llm-provider-settings'

interface MafiaSettingsProps {
  audioReady: boolean
  audioMuted: boolean
  musicVolume: number
  sfxVolume: number
  onEnableAudio: () => void
  onToggleMute: () => void
  onMusicVolume: (v: number) => void
  onSfxVolume: (v: number) => void
  onLlmSettingsChange?: (s: MafiaLlmProviderSettings) => void
}

export function MafiaSettings({
  audioReady,
  audioMuted,
  musicVolume,
  sfxVolume,
  onEnableAudio,
  onToggleMute,
  onMusicVolume,
  onSfxVolume,
  onLlmSettingsChange,
}: MafiaSettingsProps) {
  const [open, setOpen] = useState(false)
  const [llm, setLlm] = useState<MafiaLlmProviderSettings>(DEFAULT_LLM_SETTINGS)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setLlm(loadLlmProviderSettings())
  }, [])

  const persistLlm = (next: MafiaLlmProviderSettings) => {
    setLlm(next)
    saveLlmProviderSettings(next)
    onLlmSettingsChange?.(next)
    setSaved(true)
    window.setTimeout(() => setSaved(false), 1500)
  }

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        className="text-[var(--mafia-muted)] hover:text-[var(--mafia-candle)]"
        aria-expanded={open}
        aria-label="تنظیمات"
      >
        <Settings2 className="h-4 w-4" />
      </Button>

      {open && (
        <div
          className="mafia-panel absolute left-0 top-full z-40 mt-2 w-[min(100vw-2rem,22rem)] space-y-4 p-4"
          role="dialog"
          aria-label="تنظیمات مافیا"
        >
          <div className="flex items-center justify-between gap-2">
            <h2 className="mafia-display text-sm font-semibold text-[var(--mafia-candle)]">
              تنظیمات
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-[var(--mafia-muted)] transition hover:bg-black/30 hover:text-[var(--mafia-candle)]"
              aria-label="بستن تنظیمات"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <section className="space-y-3">
            <h3 className="mafia-display text-sm font-semibold text-[var(--mafia-candle)]">
              صدا
            </h3>
            {!audioReady ? (
              <Button
                type="button"
                size="sm"
                onClick={onEnableAudio}
                className="w-full bg-[var(--mafia-candle-dim)] text-[var(--mafia-ink)] hover:bg-[var(--mafia-candle)]"
              >
                <Volume2 className="ml-2 h-4 w-4" />
                فعال‌سازی موسیقی پس‌زمینه
              </Button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onToggleMute}
                  className="flex w-full items-center justify-between rounded-lg border border-[var(--mafia-border)] px-3 py-2 text-sm hover:border-[var(--mafia-border-strong)]"
                >
                  <span>صدا</span>
                  {audioMuted ? (
                    <VolumeX className="h-4 w-4 text-[var(--mafia-danger)]" />
                  ) : (
                    <Volume2 className="h-4 w-4 text-[var(--mafia-candle)]" />
                  )}
                </button>
                <label className="block space-y-1 text-xs text-[var(--mafia-muted)]">
                  موسیقی
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={musicVolume}
                    onChange={(e) => onMusicVolume(Number(e.target.value))}
                    className="w-full accent-[var(--mafia-candle)]"
                  />
                </label>
                <label className="block space-y-1 text-xs text-[var(--mafia-muted)]">
                  افکت‌ها
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={sfxVolume}
                    onChange={(e) => onSfxVolume(Number(e.target.value))}
                    className="w-full accent-[var(--mafia-candle)]"
                  />
                </label>
              </>
            )}
          </section>

          <section className="space-y-3 border-t border-[var(--mafia-border)] pt-3">
            <h3 className="mafia-display text-sm font-semibold text-[var(--mafia-candle)]">
              مدل هوش مصنوعی
            </h3>
            <p className="text-xs leading-relaxed text-[var(--mafia-muted)]">
              پیش‌فرض: مصرف اعتبار آتیرام. می‌توانید ارائه‌دهندهٔ سازگار با OpenAI خودتان را بگذارید
              (بدون کسر توکن).
            </p>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => persistLlm({ ...llm, mode: 'credits' })}
                className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs transition ${
                  llm.mode === 'credits'
                    ? 'border-[var(--mafia-candle)] bg-[var(--mafia-candle)]/10 text-[var(--mafia-candle)]'
                    : 'border-[var(--mafia-border)] text-[var(--mafia-muted)]'
                }`}
              >
                <Coins className="h-3.5 w-3.5" />
                اعتبار آتیرام
              </button>
              <button
                type="button"
                onClick={() => persistLlm({ ...llm, mode: 'byok' })}
                className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs transition ${
                  llm.mode === 'byok'
                    ? 'border-[var(--mafia-candle)] bg-[var(--mafia-candle)]/10 text-[var(--mafia-candle)]'
                    : 'border-[var(--mafia-border)] text-[var(--mafia-muted)]'
                }`}
              >
                <KeyRound className="h-3.5 w-3.5" />
                کلید خودم
              </button>
            </div>

            {llm.mode === 'byok' && (
              <div className="space-y-2">
                <label className="block space-y-1 text-xs text-[var(--mafia-muted)]">
                  Base URL
                  <input
                    type="url"
                    dir="ltr"
                    placeholder="https://api.openai.com/v1"
                    value={llm.baseUrl}
                    onChange={(e) => {
                      const baseUrl = e.target.value
                      setLlm((s) => ({ ...s, baseUrl }))
                    }}
                    className="w-full rounded-lg border border-[var(--mafia-border)] bg-black/30 px-2 py-1.5 text-sm text-[var(--mafia-ink)]"
                  />
                </label>
                <label className="block space-y-1 text-xs text-[var(--mafia-muted)]">
                  API Key
                  <input
                    type="password"
                    dir="ltr"
                    placeholder="sk-..."
                    value={llm.apiKey}
                    onChange={(e) => {
                      const apiKey = e.target.value
                      setLlm((s) => ({ ...s, apiKey }))
                    }}
                    className="w-full rounded-lg border border-[var(--mafia-border)] bg-black/30 px-2 py-1.5 text-sm text-[var(--mafia-ink)]"
                    autoComplete="off"
                  />
                </label>
                <label className="block space-y-1 text-xs text-[var(--mafia-muted)]">
                  Model
                  <input
                    type="text"
                    dir="ltr"
                    placeholder="gpt-4o-mini"
                    value={llm.model}
                    onChange={(e) => {
                      const model = e.target.value
                      setLlm((s) => ({ ...s, model }))
                    }}
                    className="w-full rounded-lg border border-[var(--mafia-border)] bg-black/30 px-2 py-1.5 text-sm text-[var(--mafia-ink)]"
                  />
                </label>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => persistLlm(llm)}
                  className="w-full bg-[var(--mafia-blood)] hover:bg-[var(--mafia-blood-bright)]"
                >
                  ذخیره
                </Button>
              </div>
            )}

            {saved && (
              <p className="text-xs text-[var(--mafia-success)]">ذخیره شد</p>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
